import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, ExternalLink, Building2, User, Briefcase, Phone, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getStatusConfig, DROPDOWN_OPTIONS } from '../../lib/appointmentStatus';

interface Appointment {
  id: string;
  time: string;
  prospect_name: string;
  job_title?: string;
  company_name?: string;
  phone?: string;
  client_name: string;
  sdr_name: string;
  status: string;
  prospect_linkedin?: string;
}

interface AppointmentListProps {
  appointments: Appointment[];
  onStatusChange: (id: string, status: string) => void;
}

const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  onStatusChange
}) => {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    const config = getStatusConfig(status);
    return `${config.bg} ${config.color} ${config.border}`;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-teal-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Heure
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Prospect
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                SDR
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Statut
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {appointments.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-400">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Aucun rendez-vous à afficher</p>
                </td>
              </tr>
            ) : (
              appointments.map((apt, index) => (
                <motion.tr
                  key={apt.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                  className="hover:bg-gray-50 transition-colors group"
                >
                  <td className="py-4 px-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="bg-cyan-50 text-cyan-700 px-3 py-1 rounded-lg text-sm font-semibold">
                        {apt.time}
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {apt.prospect_name || 'N/A'}
                          </p>
                          {apt.prospect_linkedin && (
                            <a
                              href={apt.prospect_linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-600 transition-colors"
                              title="Voir le LinkedIn"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                        {apt.job_title && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Briefcase className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <p className="text-xs text-gray-500 truncate">{apt.job_title}</p>
                          </div>
                        )}
                        {apt.company_name && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <p className="text-xs text-gray-600 truncate">{apt.company_name}</p>
                          </div>
                        )}
                        {apt.phone && (
                          <div className="flex items-center space-x-2 mt-1">
                            <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <p className="text-xs text-gray-700 font-medium">{apt.phone}</p>
                            <a
                              href={`https://app.minari.ai/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-600 hover:text-teal-700 transition-colors text-xs font-medium flex items-center space-x-1"
                              title="Ouvrir Minari"
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span>Minari</span>
                            </a>
                          </div>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          <button
                            onClick={() => navigate(`/appointments/${apt.id}`)}
                            className="text-cyan-600 hover:text-cyan-700 transition-colors text-xs font-medium flex items-center space-x-1"
                            title="Voir la fiche du RDV"
                          >
                            <FileText className="h-3 w-3" />
                            <span>Fiche RDV</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-4 whitespace-nowrap">
                    <span className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-xs font-medium border border-blue-100">
                      {apt.client_name}
                    </span>
                  </td>

                  <td className="py-4 px-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div
                        className={`${getAvatarColor(
                          apt.sdr_name
                        )} h-8 w-8 rounded-full flex items-center justify-center text-white font-semibold text-xs shadow-sm`}
                      >
                        {getInitials(apt.sdr_name)}
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{apt.sdr_name}</span>
                    </div>
                  </td>

                  <td className="py-4 px-4 whitespace-nowrap">
                    <select
                      value={getStatusConfig(apt.status).key}
                      onChange={(e) => onStatusChange(apt.id, e.target.value)}
                      className={`${getStatusColor(
                        apt.status
                      )} px-3 py-1 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all`}
                    >
                      {DROPDOWN_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AppointmentList;
